/**
 * Freigaben: die Seite der Firma.
 *
 * ORIGINALTEXT MARVIN (23.09.2026)
 *   „ich sende derzeit immer eine freigabescreenshot, was sehr lästig ist ...
 *    die freigaben über ein menüpunkt bei den firmen anzeigen lassen, plus
 *    hinweis auf die glocke."
 *
 * OBEN, WAS ZU TUN IST: eine offene Korrektur geht vor jeder Freigabe, denn
 * auf sie wartet eine Zahlung. Darunter die Freigaben, die neueste zuerst,
 * mit dem Stand „Rechnung fehlt noch" oder „Rechnung eingegangen".
 *
 * KEINE POSITION UND KEINE MENGE IN EINER FREIGABE. Die Firma rechnet aus
 * eigener Hand ab; der Server liefert dazu auch nichts.
 */

import { useEffect, useState } from 'react';
import { api, datum, type Anmelder } from '../api.ts';
import { Kopf, Karte, Leer } from '../teile/Geruest.tsx';
import { useFrisch } from '../teile/Frisch.tsx';
import { Fehlerkasten, ausFehler } from '../teile/Formular.tsx';

const GEWERK: Record<string, string> = { tiefbau: 'Tiefbau', montage: 'Montage' };

function anschrift(z: any): string {
  const nr = `${z.hausnummer ?? ''}${z.hausnummer_zusatz ?? ''}`.trim();
  return [`${z.strasse ?? ''} ${nr}`.trim(), `${z.plz ?? ''} ${z.ort_text ?? ''}`.trim()]
    .filter(Boolean).join(', ') || 'ohne Anschrift';
}

function zahl(x: any, e: string | null): string {
  if (x === null || x === undefined) return 'nicht aufgeführt';
  return `${String(Number(x)).replace('.', ',')}${e ? ' ' + e : ''}`;
}

export default function Freigaben({ ich }: { ich: Anmelder }) {
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<any>(null);
  async function laden() {
    try { setD(await api.meineFreigaben()); setFehler(null); }
    catch (f) { setFehler(ausFehler(f)); }
  }
  useEffect(() => { laden(); }, []);
  useFrisch(laden);

  const offen = (d?.korrekturen ?? []).filter((k: any) => !k.erledigt_am);
  const erledigt = (d?.korrekturen ?? []).filter((k: any) => k.erledigt_am);
  const ohneRechnung = (d?.freigaben ?? []).filter((f: any) => !f.rechnung_eingang).length;

  return (
    <>
      <Kopf titel="Freigaben"
            weg={ich.firma ? `${ich.firma}: was abgerechnet werden kann` : 'Freigaben zur Abrechnung'} />
      <div className="inhalt">
        <Fehlerkasten lage={fehler} />
        {d?.hinweis && <div className="hinweis">{d.hinweis}</div>}

        {offen.length > 0 && (
          <Karte titel={`Korrektur verlangt (${offen.length})`}>
            <p className="klein leise" style={{ marginTop: 0 }}>
              Bitte die Rechnung neu ausstellen und wie gewohnt an
              invoice@mds-innovation.com schicken. Die neue Rechnung ersetzt
              die alte. Auf jede Rechnung gehören Baustellenadresse, KLS-ID
              und SM-Nummer.
            </p>
            <div className="freigabeliste">
              {offen.map((k: any) => (
                <div key={k.id} className="freigabekarte korrektur">
                  <div className="freigabekopf">
                    <b>{k.rechnungsnummer ? `Rechnung ${k.rechnungsnummer}` : 'Rechnung ohne Nummer'}</b>
                    <span className="marke-zustand z-neu">bitte neu ausstellen</span>
                  </div>
                  <div className="klein leise">
                    {k.strasse ? anschrift(k) : ''}{k.kls_id ? `, KLS ${k.kls_id}` : ''}
                    {', '}verlangt am {datum(k.gesendet_am)}
                  </div>
                  <table className="liste korrekturtabelle">
                    <thead>
                      <tr>
                        <th>Position</th>
                        <th className="zahl">Rechnung</th>
                        <th className="zahl">richtig</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(k.positionen ?? []).map((p: any, i: number) => (
                        <tr key={i} style={{ cursor: 'default' }}>
                          <td>
                            {p.bezeichnung ?? ''}
                            {p.oz && <div className="klein leise">{p.oz}</div>}
                          </td>
                          <td className="zahl">{zahl(p.menge_rechnung, p.einheit)}</td>
                          <td className="zahl"><b>{zahl(p.menge_richtig, p.einheit)}</b></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {k.satz && <p className="klein" style={{ marginBottom: 0 }}>{k.satz}</p>}
                </div>
              ))}
            </div>
          </Karte>
        )}

        <Karte titel="Zur Abrechnung freigegeben"
               rechts={d ? <span className="klein leise">{ohneRechnung} ohne Rechnung</span> : null}>
          {!d ? <p className="klein leise">Wird geladen.</p>
            : !(d.freigaben ?? []).length ? (
              <Leer text="Noch keine Freigabe. Sobald ein Auftrag abgerechnet werden kann, steht er hier, und es kommt eine Meldung in die Glocke." />
            ) : (
              <div className="freigabeliste">
                {d.freigaben.map((f: any) => (
                  <div key={f.id} className="freigabekarte">
                    <div className="freigabekopf">
                      <b>{anschrift(f)}</b>
                      {f.rechnung_eingang
                        ? <span className="marke-zustand z-erledigt">Rechnung eingegangen</span>
                        : <span className="marke-zustand z-neu">Rechnung fehlt noch</span>}
                    </div>
                    <div className="klein leise">
                      {GEWERK[f.gewerk_gruppe] ?? f.gewerk_gruppe}
                      {f.kls_id ? `, KLS ${f.kls_id}` : ''}
                      {', '}freigegeben am {datum(f.freigegeben_am)}
                      {f.rechnung_eingang ? `, Rechnung am ${datum(f.rechnung_eingang)}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
        </Karte>

        {erledigt.length > 0 && (
          <Karte titel="Erledigte Korrekturen">
            <div className="freigabeliste">
              {erledigt.map((k: any) => (
                <div key={k.id} className="freigabekarte">
                  <div className="freigabekopf">
                    <span>{k.rechnungsnummer ? `Rechnung ${k.rechnungsnummer}` : 'Rechnung ohne Nummer'}</span>
                    <span className="marke-zustand z-erledigt">
                      ersetzt{k.neue_nummer ? ` durch ${k.neue_nummer}` : ''}
                    </span>
                  </div>
                  <div className="klein leise">
                    verlangt am {datum(k.gesendet_am)}, erledigt am {datum(k.erledigt_am)}
                  </div>
                </div>
              ))}
            </div>
          </Karte>
        )}
      </div>
    </>
  );
}
